import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface FixDiffTask {
  id: string;
  slug: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  params: {
    news_id?: string;
    period?: string;
    prompt?: string;
  };
  created_at: string;
  updated_at: string;
}

interface UseFixDiffTasksResult {
  tasks: FixDiffTask[];
  tasksByNewsId: Map<string, FixDiffTask>;
  tasksBySlug: Map<string, FixDiffTask[]>;
  isLoading: boolean;
  refetch: () => void;
}

/**
 * Hook to fetch pending/running fix_diff tasks
 * Returns tasks indexed by news_id and slug for quick lookup
 */
export function useFixDiffTasks(): UseFixDiffTasksResult {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['fixDiffTasks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('claude_tasks')
        .select('*')
        .eq('command', 'fix_diff')
        .in('status', ['pending', 'running'])
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[useFixDiffTasks] Error:', error);
        throw error;
      }

      return (data || []) as FixDiffTask[];
    },
    staleTime: 0,
    gcTime: 0,
    refetchInterval: 5000, // Poll every 5 seconds
    refetchOnWindowFocus: true,
  });

  const tasks = data || [];

  // Index by news_id for quick lookup
  const tasksByNewsId = new Map<string, FixDiffTask>();
  tasks.forEach(task => {
    if (task.params?.news_id) {
      tasksByNewsId.set(task.params.news_id, task);
    }
  });

  // Index by slug for quick lookup (multiple tasks per slug possible)
  const tasksBySlug = new Map<string, FixDiffTask[]>();
  tasks.forEach(task => {
    const existing = tasksBySlug.get(task.slug) || [];
    existing.push(task);
    tasksBySlug.set(task.slug, existing);
  });

  return {
    tasks,
    tasksByNewsId,
    tasksBySlug,
    isLoading,
    refetch,
  };
}
